/**
 * Synthesizes `<configHome>/.credentials.json` from CLAUDE_CODE_OAUTH_TOKEN so
 * the interactive `claude` TUI starts authenticated in Conveyor-managed cloud
 * environments. The interactive TUI does NOT read CLAUDE_CODE_OAUTH_TOKEN —
 * that env var is honored only in headless/CI runs — so without a credentials
 * file a fresh pod's Connected TUI lands on the login-method picker.
 *
 * Safety policy: a credentials file whose `claudeAiOauth` carries a
 * refreshToken was written by a real interactive `/login` (the OAuth code flow
 * always issues one) and is never touched. Our synthesized file never has one —
 * that absence IS the "written by Conveyor" marker. If live verification ever
 * disproves that assumption, the fallback hardening is a sidecar marker file
 * (e.g. `<configHome>/.conveyor-credentials.json` holding a hash of the token
 * we wrote) — noted here so the next person doesn't reinvent the policy.
 *
 * Decision core is pure (planCredentialsWrite) so the overwrite policy can be
 * unit-tested without fs; the effectful wrappers never throw — a GCS-FUSE
 * hiccup must not kill PtySession.start().
 */

import { chmod, mkdir, readFile, rm, writeFile } from "node:fs/promises";
import { homedir } from "node:os";
import { join } from "node:path";
import { FABLE_MODEL } from "@project/shared";
import { claudeConfigHome } from "./settings.js";

// `claude setup-token` tokens are valid for ~1 year. The exact expiry is
// unknowable from the token itself; claim a far-future expiresAt so the CLI
// never decides the credential is expired and attempts a refresh it cannot
// perform (no refreshToken). The file is re-synthesized on every spawn, so the
// window slides.
const SYNTH_TOKEN_TTL_MS = 365 * 24 * 60 * 60 * 1000;
// Rewrite our own file when it has less than this much runway left — a cheap
// staleness guard for a long-lived file on the persistent user-home mount.
const REFRESH_SKEW_MS = 30 * 24 * 60 * 60 * 1000;

export function claudeCredentialsPath(): string {
  return join(claudeConfigHome(), ".credentials.json");
}

/**
 * Only act inside Conveyor-managed cloud environments (GKE claudespace pods
 * export CLAUDESPACE_NAME in entrypoint.sh; GitHub Codespaces set
 * CODESPACE_NAME/CODESPACES) — never touch a developer's real ~/.claude.
 */
export function isConveyorCloudEnv(env: NodeJS.ProcessEnv = process.env): boolean {
  return Boolean(env.CLAUDESPACE_NAME || env.CODESPACE_NAME || env.CODESPACES);
}

export interface CredentialsWriteInput {
  isCloud: boolean;
  /** CLAUDE_CODE_OAUTH_TOKEN from the environment. */
  token: string | undefined;
  /** Current file contents; null when the file is missing. */
  existingRaw: string | null;
  now: number;
}

export type CredentialsPlan =
  | { action: "skip"; reason: "not-cloud" | "no-token" | "foreign-credentials" | "current" }
  | { action: "write"; contents: string };

interface ParsedOauth {
  accessToken: unknown;
  refreshToken: unknown;
  expiresAt: unknown;
}

function parseClaudeAiOauth(raw: string | null): ParsedOauth | null {
  if (!raw || raw.trim() === "") return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const oauth = (parsed as Record<string, unknown>).claudeAiOauth;
    if (typeof oauth !== "object" || oauth === null) return null;
    const record = oauth as Record<string, unknown>;
    return {
      accessToken: record.accessToken,
      refreshToken: record.refreshToken,
      expiresAt: record.expiresAt,
    };
  } catch {
    return null;
  }
}

/**
 * The synthesized shape is the empirical-iteration point: if a live pod's TUI
 * still shows the login picker, adjust scopes/subscriptionType HERE and
 * re-verify on a Claudespace diagnostic card.
 */
export function buildSynthesizedCredentials(token: string, now: number): string {
  return JSON.stringify({
    claudeAiOauth: {
      accessToken: token,
      expiresAt: now + SYNTH_TOKEN_TTL_MS,
      scopes: ["user:inference", "user:profile"],
      subscriptionType: "max",
    },
  });
}

/** Pure overwrite-policy core — see the decision table in the tests. */
export function planCredentialsWrite(input: CredentialsWriteInput): CredentialsPlan {
  if (!input.isCloud) return { action: "skip", reason: "not-cloud" };
  if (!input.token) return { action: "skip", reason: "no-token" };

  const contents = buildSynthesizedCredentials(input.token, input.now);
  const existing = parseClaudeAiOauth(input.existingRaw);
  // Missing, empty, unparseable, or shapeless file — ours to (re)write.
  if (!existing) return { action: "write", contents };
  // A refreshToken means a genuine interactive /login — never clobber.
  if (typeof existing.refreshToken === "string" && existing.refreshToken.length > 0) {
    return { action: "skip", reason: "foreign-credentials" };
  }
  const fresh =
    existing.accessToken === input.token &&
    typeof existing.expiresAt === "number" &&
    existing.expiresAt > input.now + REFRESH_SKEW_MS;
  // Skip when current to avoid write churn on the GCS-FUSE mount.
  if (fresh) return { action: "skip", reason: "current" };
  return { action: "write", contents };
}

async function readRaw(path: string): Promise<string | null> {
  try {
    return await readFile(path, "utf8");
  } catch {
    return null;
  }
}

// ── TUI auth readiness (login-park detection) ────────────────────────────────
//
// A Claudespace pod that boots with NO usable Claude credential — no
// CLAUDE_CODE_OAUTH_TOKEN, no ANTHROPIC_API_KEY, and no interactive `/login`
// credentials file — lands the interactive TUI on the sign-in screen (the env
// token is honored only in headless runs; see the file header). Nothing parses
// the raw TUI output, so that park is otherwise silent and unrecoverable: the
// event-loop heartbeat keeps the pod "alive" and it is eventually slept as idle
// with no signal to the team. `classifyTuiAuth` is the deterministic detector
// used at spawn time (post credential synthesis) to surface an actionable
// message instead of freezing.

export interface TuiAuthInput {
  /** Only Conveyor-managed cloud envs synthesize credentials; a dev's local
   *  `~/.claude` is never our concern. */
  isCloud: boolean;
  /** CLAUDE_CODE_OAUTH_TOKEN present in the env. */
  hasOauthToken: boolean;
  /** ANTHROPIC_API_KEY present in the env (the CLI authenticates headlessly
   *  with it — no login screen). */
  hasApiKey: boolean;
  /** The on-disk credentials file carries an accessToken (our synthesized file
   *  or a real interactive `/login`). */
  credsHasAccessToken: boolean;
}

/**
 * Pure core: will the interactive TUI start authenticated, or park at the
 * sign-in screen? Outside a cloud env we never judge (a developer's own
 * `~/.claude` owns its auth). Inside one, any of an OAuth token, an API key, or
 * an existing accessToken on disk means the TUI boots into the REPL; the
 * absence of all three is the login-park condition.
 */
export function classifyTuiAuth(input: TuiAuthInput): "ready" | "no-credential" {
  if (!input.isCloud) return "ready";
  if (input.hasOauthToken || input.hasApiKey || input.credsHasAccessToken) return "ready";
  return "no-credential";
}

export interface TuiAuthReadiness {
  ready: boolean;
  status: "ready" | "no-credential";
}

/**
 * Resolve TUI auth readiness from the live environment plus the on-disk
 * credentials file. Call this AFTER credential synthesis (ensureClaudeCredentials
 * in prepareEnvironment) so a freshly-written accessToken counts. `readIdentity`
 * is injectable for tests; it defaults to the real credentials-file reader.
 */
export async function resolveTuiAuthReadiness(
  env: NodeJS.ProcessEnv = process.env,
  readIdentity: () => Promise<CredentialsIdentity | null> = readCredentialsIdentity,
): Promise<TuiAuthReadiness> {
  const isCloud = isConveyorCloudEnv(env);
  // Skip the fs read entirely outside a cloud env — nothing to judge.
  const credsHasAccessToken = isCloud ? Boolean((await readIdentity())?.accessToken) : false;
  const status = classifyTuiAuth({
    isCloud,
    hasOauthToken: Boolean(env.CLAUDE_CODE_OAUTH_TOKEN),
    hasApiKey: Boolean(env.ANTHROPIC_API_KEY),
    credsHasAccessToken,
  });
  return { ready: status === "ready", status };
}

export interface CredentialsIdentity {
  accessToken: string | null;
  /** A refreshToken means a real interactive /login wrote the file, not us. */
  hasRefreshToken: boolean;
}

/**
 * Who is the credentials file currently authenticated as? Used by the usage
 * sampler to refuse attributing another account's gauges to this session's key
 * (the file used to be shared across the user's concurrent pods, and a manual
 * /login can still repoint it). Null when the file is missing or unparseable.
 */
export async function readCredentialsIdentity(): Promise<CredentialsIdentity | null> {
  const parsed = parseClaudeAiOauth(await readRaw(claudeCredentialsPath()));
  if (!parsed) return null;
  return {
    accessToken: typeof parsed.accessToken === "string" ? parsed.accessToken : null,
    hasRefreshToken: typeof parsed.refreshToken === "string" && parsed.refreshToken.length > 0,
  };
}

// ── Verified writes for network-backed homes ────────────────────────────────
//
// On the GCS-FUSE user-home mount a write can report success while subsequent
// reads still serve stale content (metadata cache). The CLI's startup read
// then sees the OLD config — re-surfacing the startup dialogs these seeds
// exist to suppress, or (worse) a poisoned customApiKeyResponses entry the
// sanitize pass just removed. Seen live 2026-07-12: `wrote 389B, read 309B`
// parked a card's TUI at a startup dialog for 45 minutes. Bounded
// rewrite-and-verify gives the cache time to converge before the TUI spawns;
// on exhaustion callers fall through to the previous log-and-proceed behavior.

/** Backoff between verify attempts; total worst-case wait ≈ 3.75s per file. */
const READ_BACK_DELAYS_MS = [250, 500, 1000, 2000];

export interface VerifiedWriteIo {
  write: (contents: string) => Promise<void>;
  read: () => Promise<string | null>;
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Write `contents`, then verify a read returns exactly what was written,
 * rewriting and re-reading with backoff until it matches or the delays are
 * exhausted. Returns true when a read-back matched.
 */
export async function writeWithReadBackRetry(
  io: VerifiedWriteIo,
  contents: string,
  delaysMs: readonly number[] = READ_BACK_DELAYS_MS,
): Promise<boolean> {
  const sleep = io.sleep ?? defaultSleep;
  for (let attempt = 0; ; attempt++) {
    await io.write(contents);
    if ((await io.read()) === contents) return true;
    if (attempt >= delaysMs.length) return false;
    await sleep(delaysMs[attempt]);
  }
}

function fsWriteIo(path: string, mode?: number): VerifiedWriteIo {
  return {
    write: (contents) =>
      writeFile(path, contents, mode === undefined ? "utf8" : { encoding: "utf8", mode }),
    read: () => readRaw(path),
  };
}

/**
 * Materialize CLAUDE_CODE_OAUTH_TOKEN as a credentials file for the
 * interactive TUI. Best-effort: logs and returns on any failure.
 */
export async function ensureClaudeCredentials(env: NodeJS.ProcessEnv = process.env): Promise<void> {
  const isCloud = isConveyorCloudEnv(env);
  const token = env.CLAUDE_CODE_OAUTH_TOKEN;
  // Self-heal first: a prior ANTHROPIC_API_KEY leak may have persisted an
  // "approved external API key" entry for this OAuth token in the durable
  // (GCS-FUSE) ~/.claude.json — which makes the CLI send the OAuth token via
  // x-api-key → 401 forever. Drop it before synthesizing the credentials file
  // so the OAuth auth path can take over on this boot. Independent of whether
  // the credentials file itself needs rewriting.
  if (isCloud && token) {
    await sanitizeApprovedApiKeys(token);
  }
  try {
    const path = claudeCredentialsPath();
    const plan = planCredentialsWrite({
      isCloud,
      token,
      existingRaw: await readRaw(path),
      now: Date.now(),
    });
    if (plan.action === "skip") return;
    await mkdir(claudeConfigHome(), { recursive: true });
    const verified = await writeWithReadBackRetry(fsWriteIo(path, 0o600), plan.contents);
    if (!verified) {
      process.stderr.write(
        `[conveyor-agent] claude credentials read-back still stale after retries at ${path} — TUI may land on the login picker\n`,
      );
    }
    // The GCS-FUSE CSI mount (file-mode=700) may reject chmod; it already
    // enforces owner-only access, so this is belt-and-braces for local disks.
    await chmod(path, 0o600).catch(() => {});
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    process.stderr.write(`[conveyor-agent] claude credentials sync failed: ${message}\n`);
  }
}

/** Claude Code remembers an approved/rejected external API key by the last 20
 *  characters of the key, stored in customApiKeyResponses. */
export function apiKeyApprovalSuffix(token: string): string {
  return token.slice(-20);
}

/**
 * Pure core: given the current ~/.claude.json contents and the OAuth token we
 * are injecting, drop any `customApiKeyResponses.approved` entry matching the
 * token's last-20-char suffix. Returns the sanitized JSON string, or null when
 * there is nothing to remove (no write needed).
 *
 * Such an entry can only exist through the ANTHROPIC_API_KEY leak: the pod once
 * exported the subscription OAuth token as ANTHROPIC_API_KEY and a human
 * answered "yes, use this API key" in the TUI. From then on the CLI sends the
 * OAuth token via `x-api-key` → 401 "Invalid API key · Fix external API key",
 * on every future pod for that user+project (the entry lives on the persistent
 * GCS-FUSE home). Removing it lets the synthesized OAuth credentials win again.
 */
export function planApprovedApiKeyCleanup(
  existingRaw: string | null,
  oauthToken: string,
): string | null {
  if (!oauthToken) return null;
  const config = parseClaudeJson(existingRaw);
  const responses = config.customApiKeyResponses;
  if (typeof responses !== "object" || responses === null || Array.isArray(responses)) {
    return null;
  }
  const record = responses as Record<string, unknown>;
  const approved = record.approved;
  if (!Array.isArray(approved)) return null;
  const suffix = apiKeyApprovalSuffix(oauthToken);
  const filtered = approved.filter((entry) => entry !== suffix);
  if (filtered.length === approved.length) return null;
  record.approved = filtered;
  return JSON.stringify(config);
}

/**
 * Effectful wrapper for planApprovedApiKeyCleanup. Best-effort and isolated in
 * its own try/catch so a GCS-FUSE hiccup can never block credential synthesis.
 */
async function sanitizeApprovedApiKeys(oauthToken: string): Promise<void> {
  try {
    const path = claudeJsonPath();
    const cleaned = planApprovedApiKeyCleanup(await readRaw(path), oauthToken);
    if (cleaned === null) return;
    const verified = await writeWithReadBackRetry(fsWriteIo(path), cleaned);
    process.stderr.write(
      verified
        ? "[conveyor-agent] removed poisoned customApiKeyResponses.approved entry from .claude.json\n"
        : `[conveyor-agent] approved-key sanitize read-back still stale after retries at ${path} — CLI may still see the poisoned entry\n`,
    );
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    process.stderr.write(`[conveyor-agent] claude approved-key sanitize failed: ${message}\n`);
  }
}

/**
 * Path of the CLI's main config. Lives at $HOME/.claude.json normally; when
 * CLAUDE_CONFIG_DIR is set the CLI relocates it under that directory.
 */
export function claudeJsonPath(): string {
  const configDir = process.env.CLAUDE_CONFIG_DIR;
  return configDir ? join(configDir, ".claude.json") : join(homedir(), ".claude.json");
}

/**
 * Pure decision core for the onboarding + trust seed. Returns the contents to
 * write, or null when no write is needed.
 *
 * Unlike the entrypoint's only-if-empty seeding, this MERGES into an existing
 * config: the CLI rewrites .claude.json during a first-run wizard (cache keys,
 * firstStartTime, …) WITHOUT setting hasCompletedOnboarding until the wizard
 * finishes. On a persistent user-home (GCS-FUSE) an abandoned wizard therefore
 * leaves a config that re-triggers onboarding on every subsequent pod — seen
 * live: credentials valid (`claude auth status` → loggedIn:true) yet the TUI
 * parked at the theme picker. All CLI-owned keys are preserved.
 *
 * `trustCwd` pre-accepts the folder-trust dialog for the workspace the TUI is
 * about to run in (`projects["<cwd>"].hasTrustDialogAccepted`) — the dialog is
 * the third first-run gate after theme and login, also seen parking a live
 * pod. Trusting the Conveyor-managed repo clone is definitionally correct in
 * this environment. The theme default is only applied when creating a fresh
 * config: the CLI drops unused keys on rewrite, and re-adding theme on every
 * spawn would cause a guaranteed write per spawn for zero behavior change
 * (the theme picker is gated on hasCompletedOnboarding, not theme).
 */
function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** Parse the existing .claude.json; corrupt/non-object input becomes a fresh
 *  config (the CLI treats unparseable files as fatal, so replacing is a repair). */
function parseClaudeJson(existingRaw: string | null): Record<string, unknown> {
  if (!existingRaw || existingRaw.trim() === "") return {};
  try {
    return asRecord(JSON.parse(existingRaw));
  } catch {
    return {};
  }
}

/** Pre-accept the folder-trust dialog for `trustCwd`. Returns true if changed. */
function seedWorkspaceTrust(config: Record<string, unknown>, trustCwd: string): boolean {
  const projects = asRecord(config.projects);
  const entry = asRecord(projects[trustCwd]);
  if (entry.hasTrustDialogAccepted === true) return false;
  entry.hasTrustDialogAccepted = true;
  projects[trustCwd] = entry;
  config.projects = projects;
  return true;
}

/**
 * The `/model` picker row a real interactive login receives from the CLI's
 * bootstrap fetch. Pods run on synthesized credentials whose bootstrap fetch
 * does not populate this cache, so without the seed Fable never appears in the
 * in-pod picker even though `--model claude-fable-5` spawns work. The `[1m]`
 * suffix is the CLI's 1M-context variant marker on the entitlement entry.
 */
const FABLE_MODEL_OPTION = {
  value: `${FABLE_MODEL}[1m]`,
  label: "Fable",
  description: "Fable 5 - most capable for your hardest and longest-running tasks",
};

/**
 * Ensure the CLI's cached model-picker options include a Fable row. Appends
 * only when no Fable-valued entry exists — a cache the CLI wrote from a real
 * bootstrap fetch is the account's actual entitlement set and is otherwise
 * left alone (the CLI rewrites it wholesale on a successful fetch anyway).
 */
function seedFableModelOption(config: Record<string, unknown>): boolean {
  const cache = Array.isArray(config.additionalModelOptionsCache)
    ? (config.additionalModelOptionsCache as unknown[])
    : [];
  const hasFable = cache.some((entry) => {
    if (typeof entry !== "object" || entry === null) return false;
    const value = (entry as Record<string, unknown>).value;
    return typeof value === "string" && value.toLowerCase().includes("fable");
  });
  if (hasFable) return false;
  config.additionalModelOptionsCache = [...cache, { ...FABLE_MODEL_OPTION }];
  return true;
}

/** Org/account identity the CLI keys the Fable usage-credits consent on. */
export interface OauthAccountIdentity {
  organizationUuid?: string;
  accountUuid?: string;
}

function normalizeOauthIdentity(record: Record<string, unknown>): OauthAccountIdentity | null {
  const organizationUuid =
    typeof record.organizationUuid === "string" && record.organizationUuid !== ""
      ? record.organizationUuid
      : undefined;
  const accountUuid =
    typeof record.accountUuid === "string" && record.accountUuid !== ""
      ? record.accountUuid
      : undefined;
  if (!organizationUuid && !accountUuid) return null;
  return { organizationUuid, accountUuid };
}

/** Identity from the CLI-written `oauthAccount` in ~/.claude.json, if any. */
export function extractOauthIdentity(config: Record<string, unknown>): OauthAccountIdentity | null {
  return normalizeOauthIdentity(asRecord(config.oauthAccount));
}

/**
 * Pre-answer the Fable usage-credits consent dialog. The CLI records consent
 * as `fableOverageConsentV2[<organizationUuid>] = true` (or `acct:<accountUuid>`
 * when the account has no org). Without it, a Fable turn parks an interactive
 * TUI at the consent dialog, and no-dialog contexts silently fall back to a
 * non-Fable model — both break auto cards. The UUID comes from the CLI-written
 * `oauthAccount` when this pod's CLI has already run, else from the marker
 * persisted in the shared config home (see `persistOauthIdentityMarker`).
 */
function seedFableConsent(
  config: Record<string, unknown>,
  fallbackIdentity: OauthAccountIdentity | null,
): boolean {
  const identity = extractOauthIdentity(config) ?? fallbackIdentity;
  if (!identity) return false;
  const consent = asRecord(config.fableOverageConsentV2);
  let changed = false;
  const keys = [
    ...(identity.organizationUuid ? [identity.organizationUuid] : []),
    ...(identity.accountUuid ? [`acct:${identity.accountUuid}`] : []),
  ];
  for (const key of keys) {
    if (consent[key] !== true) {
      consent[key] = true;
      changed = true;
    }
  }
  if (changed) config.fableOverageConsentV2 = consent;
  return changed;
}

export function planClaudeJsonSeed(
  existingRaw: string | null,
  trustCwd?: string,
  oauthIdentity?: OauthAccountIdentity | null,
): string | null {
  const config = parseClaudeJson(existingRaw);
  const isFresh = Object.keys(config).length === 0;
  let changed = false;
  if (config.hasCompletedOnboarding !== true) {
    config.hasCompletedOnboarding = true;
    changed = true;
  }
  // Pre-answer the "Bypass Permissions mode" warning dialog the CLI shows on
  // the first `--dangerously-skip-permissions` launch. Seeded unconditionally:
  // the flag is inert for plan-mode spawns, no human is at the keyboard in
  // cloud envs to press "Yes, I accept", and the permission mode can flip
  // between spawns sharing one persistent user-home.
  if (config.bypassPermissionsModeAccepted !== true) {
    config.bypassPermissionsModeAccepted = true;
    changed = true;
  }
  if (isFresh && typeof config.theme !== "string") {
    config.theme = "dark";
    changed = true;
  }
  if (trustCwd && seedWorkspaceTrust(config, trustCwd)) {
    changed = true;
  }
  if (seedFableModelOption(config)) {
    changed = true;
  }
  if (seedFableConsent(config, oauthIdentity ?? null)) {
    changed = true;
  }
  return changed ? JSON.stringify(config) : null;
}

/**
 * Marker persisting the CLI's `oauthAccount` identity in the SHARED config
 * home (GCS-FUSE), because ~/.claude.json is pod-local and rebuilt every boot:
 * consent recorded there — seeded or granted by a human — dies with the pod.
 * The first spawn of a fresh pod reads the marker to seed consent before the
 * CLI starts; once the CLI's bootstrap fetch writes `oauthAccount`, later
 * spawns capture it here for every future pod of this user+project.
 */
export function conveyorOauthMarkerPath(): string {
  return join(claudeConfigHome(), "conveyor-oauth-account.json");
}

function parseOauthIdentity(raw: string | null): OauthAccountIdentity | null {
  if (!raw || raw.trim() === "") return null;
  try {
    return normalizeOauthIdentity(asRecord(JSON.parse(raw)));
  } catch {
    return null;
  }
}

async function persistOauthIdentityMarker(
  configIdentity: OauthAccountIdentity | null,
  markerIdentity: OauthAccountIdentity | null,
): Promise<void> {
  try {
    if (!configIdentity) return;
    if (
      markerIdentity !== null &&
      markerIdentity.organizationUuid === configIdentity.organizationUuid &&
      markerIdentity.accountUuid === configIdentity.accountUuid
    ) {
      return;
    }
    await mkdir(claudeConfigHome(), { recursive: true });
    const verified = await writeWithReadBackRetry(
      fsWriteIo(conveyorOauthMarkerPath()),
      JSON.stringify(configIdentity),
    );
    if (verified) {
      process.stderr.write("[conveyor-agent] persisted oauth identity marker for fable consent\n");
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    process.stderr.write(`[conveyor-agent] oauth identity marker write failed: ${message}\n`);
  }
}

/**
 * Suppress the CLI's first-run wizard, the bypass-permissions warning dialog,
 * (when `trustCwd` is given) the folder-trust dialog for that workspace, and
 * the Fable picker/consent gates in Conveyor cloud envs so the spawned TUI
 * boots straight into the REPL. Runs regardless of token presence
 * — with credentials the gates are pure friction; without them the
 * post-onboarding login screen is strictly better than the full wizard.
 * Best-effort, never throws.
 */
export async function ensureClaudeOnboarding(
  env: NodeJS.ProcessEnv = process.env,
  trustCwd?: string,
): Promise<void> {
  try {
    if (!isConveyorCloudEnv(env)) return;
    const path = claudeJsonPath();
    const existingRaw = await readRaw(path);
    const markerIdentity = parseOauthIdentity(await readRaw(conveyorOauthMarkerPath()));
    // Capture the CLI-written oauthAccount into the shared-home marker so the
    // NEXT pod's first spawn can seed fable consent before its CLI ever runs.
    await persistOauthIdentityMarker(
      extractOauthIdentity(parseClaudeJson(existingRaw)),
      markerIdentity,
    );
    const contents = planClaudeJsonSeed(existingRaw, trustCwd, markerIdentity);
    if (contents === null) return;
    const verified = await writeWithReadBackRetry(fsWriteIo(path), contents);
    if (verified) {
      process.stderr.write(
        `[conveyor-agent] claude onboarding seeded${trustCwd ? ` (trust: ${trustCwd})` : ""}\n`,
      );
    } else {
      const verify = await readRaw(path);
      process.stderr.write(
        `[conveyor-agent] claude onboarding seed read-back MISMATCH at ${path} after retries: wrote ${contents.length}B, read ${verify?.length ?? 0}B — CLI may see stale config and park at a startup dialog\n`,
      );
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    process.stderr.write(`[conveyor-agent] claude onboarding seed failed: ${message}\n`);
  }
}

/**
 * Delete the credentials file only when it is ours (no refreshToken). Used
 * when a runtime key update switches the agent to ANTHROPIC_API_KEY — stale
 * subscription credentials must not keep authenticating the next TUI spawn.
 */
export async function removeConveyorCredentials(
  env: NodeJS.ProcessEnv = process.env,
): Promise<void> {
  try {
    if (!isConveyorCloudEnv(env)) return;
    const path = claudeCredentialsPath();
    const existing = parseClaudeAiOauth(await readRaw(path));
    if (existing && typeof existing.refreshToken === "string" && existing.refreshToken.length > 0) {
      return;
    }
    await rm(path, { force: true });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    process.stderr.write(`[conveyor-agent] claude credentials removal failed: ${message}\n`);
  }
}
